import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { providersService } from './providers.services';

import type { ProviderContract } from './providers.types';

/**
 * Uploads a contract file to GCS via a two-step signed URL flow:
 * 1. GET /admin/providers/signed_url  → receive upload_url + gcs_path + download_url
 * 2. PUT {upload_url} with the raw file → GCS stores the binary
 *
 * Returns a ProviderContract on success so the caller can append it to
 * the contracts list in the form state.
 *
 * NOTE: Step 1 requires the backend endpoint GET /admin/providers/signed_url
 * to be implemented. Until then, the mutation will fail with a 404 and the
 * toast error will surface to the user.
 */
export const useUploadProviderContract = () => {
  return useMutation({
    mutationFn: async (file: File): Promise<ProviderContract> => {
      const signed = await providersService.getSignedUrl(file.name, file.type);
      await providersService.uploadToGcs(signed.upload_url, file);

      return {
        file_name: file.name,
        gcs_path: signed.gcs_path,
        url: signed.download_url,
      };
    },
    onError: () => {
      toast.error('No se pudo subir el contrato. Intenta de nuevo.');
    },
  });
};
